import React, { useState } from 'react';
import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

/**
 * The Google identity FlowStock writes stock updates as (backend:
 * supabase/functions/spreadsheet-stock-decrement in the monorepo). Sellers
 * grant it access by sharing their sheet with this address as Editor.
 */
export const SHEETS_SERVICE_ACCOUNT = 'sheets-writer@flowstock-468121.iam.gserviceaccount.com';

interface Props {
  spreadsheetId?: string | null;
  /** provider_config.config_object.sheet_decrement_enabled */
  enabled: boolean;
}

/**
 * Explains automatic stock updates to a Google Sheets supplier and how to
 * switch them on: share the sheet with the service account as Editor.
 */
const SheetStockUpdatesCard: React.FC<Props> = ({ spreadsheetId, enabled }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SHEETS_SERVICE_ACCOUNT);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (e.g. insecure context); the address stays selectable.
    }
  };

  return (
    <div className="glass-card rounded-2xl p-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold text-white">Automatic stock updates</h3>
        {enabled ? (
          <span className="inline-flex items-center text-xs font-medium text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2.5 py-1">
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            Active
          </span>
        ) : (
          <span className="text-xs font-medium text-white/50 bg-white/5 border border-white/10 rounded-full px-2.5 py-1">
            Not switched on
          </span>
        )}
      </div>

      <p className="text-sm text-white/60">
        When one of your pairs sells, FlowStock lowers its quantity in your sheet, so the same pair
        can't sell twice. We only ever change the quantity cell of the row that sold, matched by SKU
        and size, and never touch anything else.
      </p>

      <div className="mt-5">
        <p className="text-sm font-medium text-white mb-2">To allow it, share your sheet with:</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 min-w-0 truncate select-all bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white">
            {SHEETS_SERVICE_ACCOUNT}
          </code>
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center px-3 py-2 text-xs font-semibold rounded-lg text-white bg-white/10 hover:bg-white/15 transition-colors flex-shrink-0"
          >
            <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>

        <ol className="mt-4 space-y-1.5 text-sm text-white/60 list-decimal list-inside">
          <li>Open your stock sheet in Google Sheets.</li>
          <li>
            Click <span className="text-white">Share</span>, paste the address above and choose{' '}
            <span className="text-white">Editor</span>. Viewer is not enough.
          </li>
          <li>Untick "Notify people" and click Share.</li>
        </ol>

        {spreadsheetId && (
          <a
            href={`https://docs.google.com/spreadsheets/d/${encodeURIComponent(spreadsheetId)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center text-sm font-medium text-orange-400 hover:text-orange-300"
          >
            Open my sheet
            <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-1" />
          </a>
        )}
      </div>

      {!enabled && (
        <div className="mt-5 flex items-start bg-white/5 border border-white/10 rounded-xl p-3">
          <InformationCircleIcon className="h-5 w-5 text-white/40 flex-shrink-0 mt-0.5" />
          <p className="ml-2 text-xs text-white/50">
            After sharing, ask the FlowStock team to switch automatic updates on for your sheet. If
            something can't be updated (for example a missing row or a quantity that isn't a
            number), we email you instead of guessing.
          </p>
        </div>
      )}
    </div>
  );
};

export default SheetStockUpdatesCard;
